/**
 * Moonou Card Swatches — click a color swatch on a product card to swap
 * the card's preview image to that variant's image (instead of navigating).
 * The swap slides like a mini carousel: the old image slides out, the new
 * one slides in — direction follows the swatch position (a swatch further
 * right slides the new image in from the right). Card links (media + title)
 * are re-pointed to the variant URL so the next click lands on the product
 * page with the color preselected.
 *
 * Swatch <li> elements carry data-swatch-image / data-swatch-url
 * (see snippets/product-card.liquid). Swatches without a variant image
 * keep their default link behavior.
 */
(() => {
  if (window.__moonouCardSwatches) return;
  window.__moonouCardSwatches = true;

  const SLIDE_DURATION = 350;

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const preload = (src) =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = src;
    });

  const setImageSrc = (image, src) => {
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = src;
  };

  const slideSwap = (mediaLink, image, src, direction) => {
    const clone = image.cloneNode(false);
    clone.classList.add('mcs-clone');
    clone.classList.remove('mcs-anim');
    setImageSrc(clone, src);
    clone.style.transform = `translateX(${direction * 100}%)`;
    mediaLink.classList.add('mcs-slide-wrap');
    mediaLink.appendChild(clone);

    const finish = () => {
      setImageSrc(image, src);
      image.classList.remove('mcs-anim');
      // theme.css transitions transform on .media>img (fine pointers) —
      // disable it inline so the reset from -100% to 0 doesn't animate
      // as a visible second slide
      image.style.transition = 'none';
      image.style.transform = '';
      void image.offsetWidth; // force reflow: commit the reset while transitions are off
      // let the swapped src paint before removing the clone (avoids a flash)
      requestAnimationFrame(() => {
        image.style.transition = '';
        clone.remove();
        mediaLink.classList.remove('mcs-slide-wrap');
        delete mediaLink.dataset.mcsAnimating;
      });
    };

    // double rAF so the clone's start transform is committed before animating
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        image.classList.add('mcs-anim');
        clone.classList.add('mcs-anim');
        image.style.transform = `translateX(${direction * -100}%)`;
        clone.style.transform = 'translateX(0)';
      });
    });

    let done = false;
    const once = () => {
      if (done) return;
      done = true;
      finish();
    };
    clone.addEventListener('transitionend', once, { once: true });
    setTimeout(once, SLIDE_DURATION + 150); // safety net if transitionend never fires
  };

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('li[data-swatch-image] .color-swatch');
    if (!swatch) return;

    const item = swatch.closest('li[data-swatch-image]');
    const card = swatch.closest('.product-card');
    if (!card) return;

    event.preventDefault();

    const mediaLink = card.querySelector('.product-card__media > a.media');
    const image = mediaLink ? mediaLink.querySelector(':scope > img') : null;

    if (swatch.classList.contains('is-selected')) return;
    if (mediaLink && mediaLink.dataset.mcsAnimating) return;

    // direction: position of the clicked swatch relative to the current one
    const list = item.parentElement;
    const items = Array.from(list.children);
    const selected = list.querySelector('.color-swatch.is-selected');
    const prevIndex = selected ? items.indexOf(selected.closest('li')) : 0;
    const direction = items.indexOf(item) >= prevIndex ? 1 : -1;

    const url = item.dataset.swatchUrl;
    if (url) {
      if (mediaLink) mediaLink.setAttribute('href', url);
      card.querySelectorAll('a.product-card__title').forEach((link) => {
        link.setAttribute('href', url);
      });
    }

    card.querySelectorAll('.color-swatch.is-selected').forEach((el) => {
      el.classList.remove('is-selected');
    });
    swatch.classList.add('is-selected');

    if (!image) return;

    const src = item.dataset.swatchImage;
    if (prefersReducedMotion()) {
      setImageSrc(image, src);
      return;
    }

    mediaLink.dataset.mcsAnimating = 'true';
    preload(src).then((ok) => {
      if (!ok) {
        setImageSrc(image, src);
        delete mediaLink.dataset.mcsAnimating;
        return;
      }
      slideSwap(mediaLink, image, src, direction);
    });
  });
})();
